import { Box, Typography } from '@material-ui/core';
import { makeStyles, Theme as MuiTheme } from '@material-ui/core/styles';
import React, { useEffect, useState } from 'react';
import { PopularThemes } from '../../components/PopularThemes';
import { SubmitControl } from '../../components/SubmitControl';
import useSnackbar from '../../hooks/useSnackbar';
import { ITheme } from '../../services/types';
import ThemeList from './ThemeList';
import useThemes from '../../hooks/queries/useThemes';

const useStyles = makeStyles((theme: MuiTheme) => ({
  wrap: {
    maxWidth: 960,
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  popularThemes: {
    marginBottom: theme.spacing(4),
  },
  submitControl: {
    marginBottom: theme.spacing(3),
  },
}));

const Theme = (): JSX.Element => {
  const classes = useStyles();
  const snackbar = useSnackbar();

  const { themes, themeMutation, popularThemeMutation, error } = useThemes();

  const [popularThemes, setPopularThemes] = useState<ITheme[]>([]);
  const [restThemes, setRestThemes] = useState<ITheme[]>([]);
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const getAssortedThemes = (themes: ITheme[]): ITheme[][] => {
    const popular: ITheme[] = [];
    const rest: ITheme[] = [];
    themes.forEach(t => (t.isPopular ? popular : rest).push({ ...t }));

    // sort by the order property and normalize them.
    popular.sort((t1, t2) => (t1.order > t2.order ? 1 : -1)).forEach((t, i) => (t.order = i + 1));

    return [popular, rest];
  };

  useEffect(() => {
    if (themes) {
      const [popular, rest] = getAssortedThemes(themes);

      setPopularThemes(popular);
      setRestThemes(rest);
    }
  }, [themes]);

  const handlePopularChange = (theme: ITheme) => {
    const payload = { ...theme, isPopular: !theme.isPopular };
    themeMutation.mutate(payload);
  };

  const handlePopularOrderChange = (themes: ITheme[]) => {
    setPopularThemes(themes);
  };

  const handleEditClick = () => setIsEditing(true);

  const handleCancelClick = () => {
    if (!themes) {
      return;
    }

    const [popular] = getAssortedThemes(themes);

    setPopularThemes(popular);
    setIsEditing(false);
  };

  const handleSubmitClick = async () => {
    await popularThemeMutation.mutateAsync(popularThemes);
    setIsEditing(false);
  };

  if (error) {
    snackbar.error(error.message);
  }

  return (
    <Box className={classes.wrap}>
      <Box display="flex" marginBottom={3} alignItems="center">
        <Typography variant="h6">Popular Themes</Typography>
        <SubmitControl
          mode={isEditing ? 'edit' : 'view'}
          editTitle={'CHANGE ORDER'}
          onEditClick={handleEditClick}
          onCancelClick={handleCancelClick}
          onSubmitClick={handleSubmitClick}
        />
      </Box>
      {popularThemes && (
        <PopularThemes themes={popularThemes} isEditing={isEditing} onOrderChange={handlePopularOrderChange} className={classes.popularThemes} />
      )}
      <Typography variant="h6" className={classes.title}>
        All Themes
      </Typography>
      <ThemeList
        popularThemes={popularThemes}
        restThemes={restThemes}
        isEditing={isEditing}
        onPopularToggle={handlePopularChange}
        onPopularOrderChange={handlePopularOrderChange}
      />
    </Box>
  );
};

export default Theme;
